import { Component, OnInit, Injectable, ChangeDetectorRef } from '@angular/core';
import * as _ from 'lodash';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { OperationService } from '../operation.service';
import { FormGroup, FormBuilder, FormControl, FormArray, Validators } from '@angular/forms';
import {
  OperationContactsService,
  OperationContactPostBody,
  OperationContactPutBody
} from '../operation-contacts.service';
import { UserService } from '@app/modules/user/user.service';
import { User } from '@app/modules/user/user';
import { NotificationService } from '@app/modules/notification/notification.service';
import { OperationResolver } from '../operation-resolver';
import {
  OperationPutBody,
  OperationCallRepPostBody,
  Operation,
  OperationManager,
  OperationGroup,
  OperationCallRep
} from '../operation';
import { OperationContact } from '../operation-contact/operation-contact';
import { NotificationRecipientService } from '@app/modules/notification/notification-recipient/notification-recipient.service';
import { NotificationType } from '@app/modules/notification/notification';
import { ToastrService } from 'ngx-toastr';
import { ModalController } from '@ionic/angular';
import { UserRoles } from '@app/modules/user/user';

@Component({
  providers: [
    NotificationRecipientService,
    OperationService,
    OperationContactsService,
    OperationResolver,
    ToastrService
  ],
  selector: 'app-operation-form',
  templateUrl: './operation-form.component.html',
  styleUrls: ['./operation-form.component.scss'],
  standalone: false
})
@Injectable()
export class OperationFormComponent implements OnInit {
  addOperationGroupModal: ModalController;
  addOperationGroupModalOn: boolean = false;
  addOperationGroupFormControl: FormGroup;
  userRoles: typeof UserRoles = UserRoles;
  availableUsers: User[];
  availableManagers: User[];
  operation: Operation;
  mode: any = {
    add: false,
    edit: false,
    view: false
  };
  notificationsLoaded: boolean = false;
  notificationTypes: NotificationType[];
  operationForm!: FormGroup;
  operation$: Observable<Operation>;
  operationManagers: OperationManager[] = [];
  operationManagersOriginal: string[] = [];
  operationManagersToAdd: OperationManager[] = [];
  operationManagersToRemove: string[] = [];
  operationCallReps: OperationCallRep[] = [];
  operationCallRepsOriginal: string[] = [];
  operationCallRepsToAdd: OperationCallRep[] = [];
  operationCallRepsToRemove: string[] = [];
  operationContacts$: Observable<OperationContact[]>;
  operationContactsOriginal: string[] = [];
  operationContacts: OperationContact[] = [];
  operationContactsToAdd: OperationContact[] = [];
  operationContactsToEdit: OperationContact[] = [];
  operationContactsToRemove: string[] = [];
  operationGroups: OperationGroup[] = [];
  operationGroupsLoading: boolean = false;
  isSaving: boolean = false;
  user: User;

  get selectableOperationGroups(): OperationGroup[] {
    const operationGroups = Array.isArray(this.operationGroups) ? this.operationGroups : [];
    const activeOperationGroups = operationGroups.filter(
      (operationGroup: OperationGroup) => Number(operationGroup?.operationGroupActive) !== 0
    );

    if (this.mode.add) {
      return activeOperationGroups;
    }

    const selectedOperationGroupId =
      this.operationForm?.get('operation.operationGroupId')?.value || this.operation?.operationGroupId;
    const selectedOperationGroup = operationGroups.find(
      (operationGroup: OperationGroup) => operationGroup?.operationGroupId === selectedOperationGroupId
    );

    if (
      selectedOperationGroup &&
      Number(selectedOperationGroup?.operationGroupActive) === 0 &&
      !activeOperationGroups.some(
        (operationGroup: OperationGroup) => operationGroup?.operationGroupId === selectedOperationGroup.operationGroupId
      )
    ) {
      return [selectedOperationGroup, ...activeOperationGroups];
    }

    return activeOperationGroups;
  }

  constructor(
    private fb: FormBuilder,
    private notificationService: NotificationService,
    private notificationRecipientService: NotificationRecipientService,
    private operationService: OperationService,
    private operationContactsService: OperationContactsService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private userService: UserService,
    private cdr: ChangeDetectorRef
  ) {}
  ngOnInit() {
    this.user = this.route.snapshot.data.user;
    if (this.route.snapshot.data.mode == 'add') {
      this.mode.add = true;
    } else if (this.route.snapshot.data.mode == 'edit') {
      this.mode.edit = true;
    } else if (this.route.snapshot.data.mode == 'view') {
      this.mode.view = true;
    }

    this.operationGroups = this.mode.add
      ? this.getVisibleOwnershipOperationGroupsFromUser()
      : Array.isArray(this.user?.operationGroups)
        ? this.user.operationGroups
        : [];
    this.loadCachedOperationGroups();
    this.loadOperationGroups();

    this.userService.getActiveUsers().subscribe((users: User[]) => {
      this.availableUsers = users;
    });

    if (!this.mode.view) {
      this.notificationService.getNotificationTypes().subscribe((notificationTypes: NotificationType[]) => {
        this.notificationTypes = notificationTypes;
      });
    }
    if (this.mode.edit || this.mode.view) {
      this.operation = this.route.snapshot.data.operation;
      this.createForm();
    } else if (this.mode.add) {
      this.operationService.addNewOperation().subscribe((data: Operation) => {
        let operation = data;
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            operationId: operation.operationId
          },
          queryParamsHandling: 'merge'
        });
        this.operation = data;
        this.createForm();
        this.updateOperationContacts();
      });
    }
    /**
     * We can pull in our operationId from the operation resolver
     * if we're coming from the listing route. If we load by sidebar
     * we will listen for the change.
     */
    if (this.route.snapshot.data.operation) {
      let operationId = this.route.snapshot.data.operation.operationId;
      this.operationService.getOperationByOperationId(operationId).subscribe((operation: Operation) => {
        this.cleanData();
        this.updateOperation(operation);
        this.updateOperationContacts();
      });
    }
  }
  operationChangeEventHandler(operationId: string) {
    this.operationService.getOperationByOperationId(operationId).subscribe((operation: Operation) => {
      this.cleanData();
      this.updateOperation(operation);
      this.updateOperationContacts();
    });
  }
  cleanData() {
    this.notificationsLoaded = false;
    let formArray = this.operationForm.controls.operationContacts as FormArray;
    formArray.clear();
    this.operationContacts = [];
    this.operationContactsOriginal = [];
  }
  updateOperationContacts() {
    this.cleanData();
    let formArray = this.operationForm.controls.operationContacts as FormArray;
    this.operationContactsService
      .getOperationContactsByOperationId(this.operation.operationId)
      .pipe(
        take(1),
        map((operationContacts: OperationContact[]) => {
          if (operationContacts !== null) {
            if (this.mode.view) {
              this.operationContacts = operationContacts;
              this.notificationsLoaded = true;
            } else {
              this.operationContacts = [];
              operationContacts.forEach((operationContact: OperationContact, idx: number) => {
                operationContact.operationContactOrder = idx + 1;
                let contactFormGroup = this.fb.group({});
                contactFormGroup.addControl('operationContactFirstName', this.fb.control(''));
                contactFormGroup.addControl('operationContactLastName', this.fb.control(''));
                contactFormGroup.addControl('operationContactTitle', this.fb.control(''));
                contactFormGroup.addControl('operationContactCountryCode', this.fb.control('1'));
                contactFormGroup.addControl('operationContactPhoneNumber', this.fb.control(''));
                contactFormGroup.addControl('operationContactAreaCode', this.fb.control(''));
                contactFormGroup.addControl('operationContactEmail', this.fb.control(''));
                contactFormGroup.addControl(
                  'operationContactOrder',
                  this.fb.control({
                    value: idx + 1,
                    disabled: true
                  })
                );
                formArray.push(contactFormGroup);
                this.operationContacts.push(operationContact);

                var notificationTypesArray = new Array();
                this.notificationRecipientService
                  .getNotificationRecipientByOperationContactId(operationContact.operationContactId)
                  .pipe(
                    take(1),
                    map((notificationTypes: NotificationType[]) => {
                      let notificationsFormControlArray = this.fb.array<FormGroup>([]);
                      if (notificationTypes !== null) {
                        // Get the notification types assigned to ops. contact
                        notificationTypes.forEach((notificationType: NotificationType) => {
                          notificationTypesArray.push(notificationType.notificationTypeId);
                        });
                        for (let i = 0; i < this.notificationTypes.length; i++) {
                          let newFormGroup = this.fb.group({});
                          let notificationTypeId = this.notificationTypes[i].notificationTypeId;
                          if (notificationTypesArray.indexOf(this.notificationTypes[i].notificationTypeId) != -1) {
                            var newControl = new FormControl(true);
                          } else {
                            var newControl = new FormControl(false);
                          }
                          newFormGroup.addControl(notificationTypeId, newControl);
                          notificationsFormControlArray.push(newFormGroup);
                        }
                      } else {
                        for (let i = 0; i < this.notificationTypes.length; i++) {
                          let newFormGroup = this.fb.group({});
                          let notificationTypeId = this.notificationTypes[i].notificationTypeId;
                          var newControl = new FormControl(true);
                          newFormGroup.addControl(notificationTypeId, newControl);
                          notificationsFormControlArray.push(newFormGroup);
                        }
                      }
                      contactFormGroup.addControl('operationContactNotifications', notificationsFormControlArray);

                      var formGroup = formArray.controls[idx] as FormGroup;
                      formGroup.controls.operationContactFirstName.setValue(operationContact.operationContactFirstName),
                        formGroup.controls.operationContactLastName.setValue(operationContact.operationContactLastName),
                        formGroup.controls.operationContactTitle.setValue(operationContact.operationContactTitle),
                        formGroup.controls.operationContactEmail.setValue(operationContact.operationContactEmail),
                        formGroup.controls.operationContactCountryCode.setValue(
                          operationContact.operationContactCountryCode || '1'
                        ),
                        formGroup.controls.operationContactAreaCode.setValue(operationContact.operationContactAreaCode),
                        formGroup.controls.operationContactPhoneNumber.setValue(
                          this.formatPhoneInputValue(operationContact.operationContactPhoneNumber)
                        );
                      formGroup.controls.operationContactOrder.setValue(operationContact.operationContactOrder);
                    })
                  )
                  .subscribe(() => {
                    this.operationContactsOriginal.push(operationContact.operationContactId);
                    /**
                     * Since subscriptions finish in reverse order
                     * we count down the index to zero and then
                     * set our flag
                     */
                    if (this.operationContactsOriginal.length == formArray.controls.length) {
                      this.notificationsLoaded = true;
                    }
                  });
              });
            }
            return operationContacts;
          } else {
            this.addAdditionalOperationContact();
            this.notificationsLoaded = true;
          }
        })
      )
      .subscribe(() => {});
  }

  updateOperation(operation: Operation | Operation[]) {
    const operationRecord = Array.isArray(operation) ? operation[0] : operation;
    if (!operationRecord) {
      return;
    }
    const existingOperationGroupId =
      this.operationForm?.get('operation.operationGroupId')?.value || this.operation?.operationGroupId || '';
    const nextOperationGroupId = operationRecord.operationGroupId || existingOperationGroupId;

    this.operation = {
      ...this.operation,
      ...operationRecord,
      operationGroupId: nextOperationGroupId
    };
    var operationFormControls = this.operationForm.get('operation') as FormGroup;
    operationFormControls.controls.operationId.setValue(this.operation.operationId);
    operationFormControls.controls.operationName.setValue(this.operation.operationName);
    operationFormControls.controls.operationGroupId.setValue(nextOperationGroupId);
    operationFormControls.controls.operationAddress.setValue(this.operation.operationAddress);
    operationFormControls.controls.operationCity.setValue(this.operation.operationCity);
    operationFormControls.controls.operationState.setValue(this.operation.operationState);
    operationFormControls.controls.operationZip.setValue(this.operation.operationZip);
    operationFormControls.controls.operationCountryCode.setValue(this.operation.operationCountryCode || '1');
    operationFormControls.controls.operationAreaCode.setValue(this.operation.operationAreaCode);
    operationFormControls.controls.operationPhoneNumber.setValue(
      this.formatPhoneInputValue(this.operation.operationPhoneNumber)
    );
    operationFormControls.controls.operationActive.setValue(this.operation.operationActive ? '1' : '0');
  }

  private loadOperationGroups() {
    this.operationGroupsLoading = true;

    if (this.mode.add) {
      this.operationGroups = this.getVisibleOwnershipOperationGroupsFromUser();
      this.operationGroupsLoading = false;
      return;
    }

    this.operationService.getAllOperationGroups().subscribe({
      next: (operationGroups: OperationGroup[]) => {
        const safeOperationGroups = Array.isArray(operationGroups) ? operationGroups : [];
        if (!safeOperationGroups.length) {
          this.loadFallbackOperationGroups();
          return;
        }

        this.operationGroups = safeOperationGroups;
        this.operationGroupsLoading = false;
      },
      error: () => this.loadFallbackOperationGroups()
    });
  }

  private loadCachedOperationGroups() {
    if (this.mode.add) {
      return;
    }

    const cachedOperationGroups = this.readCachedOperationGroups();
    if (!cachedOperationGroups.length) {
      return;
    }

    this.operationGroups = cachedOperationGroups;
  }

  private getVisibleOwnershipOperationGroupsFromUser(): OperationGroup[] {
    const visibleOperationGroups = Array.isArray(this.user?.operationGroups) ? this.user.operationGroups : [];

    return visibleOperationGroups
      .filter((operationGroup: OperationGroup) => Number(operationGroup?.operationGroupActive) !== 0)
      .map((operationGroup: OperationGroup) => ({
        ...operationGroup,
        operations: Array.isArray(operationGroup?.operations)
          ? operationGroup.operations.filter((operation: Operation) => Number((operation as any)?.operationActive) !== 0)
          : operationGroup.operations
      }))
      .filter((operationGroup: OperationGroup) => {
        return !Array.isArray(operationGroup?.operations) || operationGroup.operations.length > 0;
      });
  }

  private readCachedOperationGroups(): OperationGroup[] {
    const cachedOperationGroupsRaw = localStorage.getItem('operationGroups');
    if (!cachedOperationGroupsRaw) {
      return [];
    }

    try {
      const cachedOperationGroups = JSON.parse(cachedOperationGroupsRaw);
      const safeOperationGroups = Array.isArray(cachedOperationGroups) ? cachedOperationGroups : [];
      return this.mode.add
        ? safeOperationGroups.filter((operationGroup: OperationGroup) => Number(operationGroup?.operationGroupActive) !== 0)
        : safeOperationGroups;
    } catch (_error) {
      return [];
    }
  }

  private loadFallbackOperationGroups() {
    this.operationService.getOperationGroups().subscribe((operationGroups: OperationGroup[]) => {
      const safeOperationGroups = Array.isArray(operationGroups) ? operationGroups : [];
      if (!safeOperationGroups.length) {
        this.operationGroupsLoading = false;
        return;
      }

      this.operationGroups = safeOperationGroups;
      this.operationGroupsLoading = false;
    });
  }

  addAdditionalOperationContact() {
    let formArray = this.operationForm.controls.operationContacts as FormArray;
    var count = formArray.length;

    let contactFormGroup = this.fb.group({});
    contactFormGroup.addControl('operationContactFirstName', this.fb.control(''));
    contactFormGroup.addControl('operationContactLastName', this.fb.control(''));
    contactFormGroup.addControl('operationContactTitle', this.fb.control(''));
    contactFormGroup.addControl('operationContactCountryCode', this.fb.control('1'));
    contactFormGroup.addControl('operationContactPhoneNumber', this.fb.control(''));
    contactFormGroup.addControl('operationContactAreaCode', this.fb.control(''));
    contactFormGroup.addControl('operationContactEmail', this.fb.control(''));
    contactFormGroup.addControl(
      'operationContactOrder',
      this.fb.control({
        value: count + 1,
        disabled: true
      })
    );
    formArray.push(contactFormGroup);

    let notificationsFormControlArray = this.fb.array<FormGroup>([]);
    let i;
    for (i = 0; i < this.notificationTypes.length; i++) {
      let newFormGroup = this.fb.group({});
      let notificationTypeId = this.notificationTypes[i].notificationTypeId;
      var newControl = new FormControl(true);
      newFormGroup.addControl(notificationTypeId, newControl);
      notificationsFormControlArray.push(newFormGroup);
    }
    contactFormGroup.addControl('operationContactNotifications', notificationsFormControlArray);

    if (this.operationContacts.length == formArray.length) {
      return;
    }

    this.operationContacts.push({
      operationContactId: null,
      operationContactOrder: this.operationContacts.length + 1
    });
  }

  private createForm() {
    this.operationForm = this.fb.group({
      operation: this.fb.group({
        operationActive: this.fb.control('1', [Validators.required]),
        operationId: this.fb.control(this.operation.operationId, [Validators.required]),
        operationGroupId: this.fb.control(this.operation.operationGroupId, [Validators.required]),
        operationName: this.fb.control(this.operation.operationName, [Validators.required]),
        operationAddress: this.fb.control(this.operation.operationAddress),
        operationCity: this.fb.control(this.operation.operationCity),
        operationState: this.fb.control(this.operation.operationState),
        operationZip: this.fb.control(this.operation.operationZip),
        operationCountryCode: this.fb.control(this.operation.operationCountryCode || '1'),
        operationAreaCode: this.fb.control(this.operation.operationAreaCode),
        operationPhoneNumber: this.fb.control(this.formatPhoneInputValue(this.operation.operationPhoneNumber), [
          Validators.pattern(/^[0-9-]{7,}\d*$/)
        ])
      }),
      operationContacts: this.fb.array([]),
      operationManagers: this.fb.array([]),
      operationCallReps: this.fb.array([])
    });
  }

  operationPutFactory(formSubmission: any): OperationPutBody {
    try {
      var operationCountryCode = '';
      var operationAreaCode = '';
      if (formSubmission.operation.operationCountryCode) {
        operationCountryCode = formSubmission.operation.operationCountryCode.toString();
      }
      if (formSubmission.operation.operationAreaCode) {
        operationAreaCode = formSubmission.operation.operationAreaCode.toString();
      }
      var payload = {
        operationName: formSubmission.operation.operationName,
        operationGroupId: formSubmission.operation.operationGroupId,
        operationAddress: formSubmission.operation.operationAddress ? formSubmission.operation.operationAddress : '',
        operationCity: formSubmission.operation.operationCity ? formSubmission.operation.operationCity : '',
        operationState: formSubmission.operation.operationState ? formSubmission.operation.operationState : '',
        operationZip: formSubmission.operation.operationZip ? formSubmission.operation.operationZip : '',
        operationCountryCode: operationCountryCode ? operationCountryCode : '1',
        operationAreaCode: operationAreaCode ? operationAreaCode : '',
        operationPhoneNumber: this.formatPhoneInputValue(formSubmission.operation.operationPhoneNumber),
        operationActive:
          formSubmission.operation.operationActive === 0 || formSubmission.operation.operationActive === '0' ? 0 : 1
      };
      return <OperationPutBody>payload;
    } catch {
      throw 'Had a problem validating data in the operation form factory';
    }
  }

  operationGroupOnSelect(event: any) {
    let operationGroupId = event.detail.value;
    this.operation.operationGroupId = operationGroupId;
    this.operationForm?.get('operation.operationGroupId')?.setValue(operationGroupId);
  }
  operationCallRepPostFactory(formSubmission: any): OperationCallRepPostBody {
    try {
      /**
       * Need processing for the user id here
       */
      var payload = {
        operationId: formSubmission.operation.operationId,
        userId: this.user.userId
      };
      return <OperationCallRepPostBody>payload;
    } catch {
      throw 'Had a problem validating data in the call rep factory';
    }
  }
  operationContactPutFactory(formContact: any): OperationContactPutBody {
    try {
      var operationCountryCode = '';
      var operationAreaCode = '';
      if (formContact.operationContactCountryCode) {
        operationCountryCode = formContact.operationContactCountryCode.toString();
      }
      if (formContact.operationContactAreaCode) {
        operationAreaCode = formContact.operationContactAreaCode.toString();
      }
      var payload = {
        operationContactOrder: formContact.operationContactOrder,
        operationContactFirstName: formContact.operationContactFirstName,
        operationContactLastName: formContact.operationContactLastName,
        operationContactTitle: formContact.operationContactTitle || '',
        operationContactCountryCode: operationCountryCode || '1',
        operationContactAreaCode: operationAreaCode || '',
        operationContactPhoneNumber: this.formatPhoneInputValue(formContact.operationContactPhoneNumber),
        operationContactEmail: formContact.operationContactEmail,
        operationContactActive: 1
      };
      return <OperationContactPutBody>payload;
    } catch (err) {
      console.log(err);
      throw 'Had a problem validating data in the operation contact put factory';
    }
  }
  operationContactPostFactory(formContact: any): OperationContactPostBody {
    try {
      var payload = {
        operationContactFirstName: formContact.operationContactFirstName,
        operationContactLastName: formContact.operationContactLastName,
        operationContactTitle: formContact.operationContactTitle,
        operationContactCountryCode: (formContact.operationContactCountryCode || '1').toString(),
        operationContactAreaCode: formContact.operationContactAreaCode,
        operationContactPhoneNumber: this.formatPhoneInputValue(formContact.operationContactPhoneNumber),
        operationContactEmail: formContact.operationContactEmail,
        operationContactOrder: formContact.operationContactOrder,
        operationContactActive: 1
      };
      return <OperationContactPostBody>payload;
    } catch {
      throw 'Had a problem validating data in the call rep factory';
    }
  }
  onCancelSubmit() {
    // display a prompt to confirm
    if (confirm('Are you sure?')) {
      window.location.reload();
    }
    // do nothing
  }

  private navigateToSubmittedOperationGroup(operationGroupId?: string) {
    const targetOperationGroupId =
      operationGroupId || this.operationForm?.get('operation.operationGroupId')?.value || this.operation?.operationGroupId;

    if (targetOperationGroupId) {
      this.operation.operationGroupId = targetOperationGroupId;
      this.router.navigate(['/clients', targetOperationGroupId]);
      return;
    }

    this.router.navigate(['/clients']);
  }

  private handleOperationSaveSuccess(operationGroupId?: string) {
    this.toastr.success('Successfully saved operation');
    this.operationContactsToRemove = [];
    this.userService.updateOperations(this.user).then(() => {
      this.navigateToSubmittedOperationGroup(operationGroupId);
    });
  }

  onFormSubmit() {
    if (this.isSaving || !this.validateControls()) {
      return;
    }

    this.isSaving = true;
    const formSubmission = this.operationForm.getRawValue();
    const submittedOperationGroupId = formSubmission?.operation?.operationGroupId || this.operation?.operationGroupId;
    this.operationContactsToAdd = this.operationContacts.filter(
      (operationContact: OperationContact) => !this.operationContactsOriginal.includes(operationContact.operationContactId)
    );
    this.operationContactsToEdit = this.operationContacts.filter(
      (operationContact: OperationContact) => !this.operationContactsToAdd.includes(operationContact)
    );

    const contactRequests: Observable<any>[] = [];

    this.operationContactsToAdd.forEach((operationContact: OperationContact, index: number) => {
      const formContact = this.getSubmittedOperationContact(formSubmission.operationContacts, operationContact, index);
      const operationContactPost = this.operationContactPostFactory(formContact);
      contactRequests.push(
        this.operationContactsService.addOperationContactByOperationId(this.operation.operationId, operationContactPost).pipe(
          switchMap((data: any) => {
            if (!data?.operationContactId) {
              return throwError(() => new Error('Operation contact was not created.'));
            }
            this.toastr.success('Operation contact successfully added');
            return this.saveOperationContactNotificationRecipients(data.operationContactId, formContact);
          })
        )
      );
    });

    this.operationContactsToEdit.forEach((operationContact: OperationContact, index: number) => {
      const formContact = this.getSubmittedOperationContact(formSubmission.operationContacts, operationContact, index);
      const operationContactPut = this.operationContactPutFactory(formContact);
      contactRequests.push(
        this.operationContactsService
          .editOperationContactByOperationContactId(
            this.operation.operationId,
            operationContact.operationContactId,
            operationContactPut
          )
          .pipe(
            switchMap(() =>
              this.saveOperationContactNotificationRecipients(operationContact.operationContactId, formContact)
            )
          )
      );
    });

    this.operationContactsToRemove
      .filter((operationContactId: string) => !!operationContactId)
      .forEach((operationContactId: string) => {
        contactRequests.push(
          this.operationContactsService
            .deactivateOperationContactByOperationContactId(this.operation.operationId, operationContactId)
            .pipe(tap(() => this.toastr.success('Successfully removed operation contact')))
        );
      });

    const operationPut = this.operationPutFactory(formSubmission);
    const prerequisiteRequests = contactRequests.length ? forkJoin(contactRequests) : of([]);

    prerequisiteRequests
      .pipe(
        switchMap(() => this.operationService.editOperationByOperationId(this.operation.operationId, operationPut)),
        finalize(() => {
          this.isSaving = false;
        })
      )
      .subscribe({
        next: () => this.handleOperationSaveSuccess(submittedOperationGroupId),
        error: () => {
          this.toastr.error('Facility was not fully saved. Your entries are still here; please review them and try again.');
        }
      });
  }

  private getSubmittedOperationContact(
    submittedContacts: any[],
    operationContact: OperationContact,
    fallbackIndex: number
  ): any {
    const orderIndex = Number(operationContact?.operationContactOrder) - 1;
    return submittedContacts[orderIndex >= 0 ? orderIndex : fallbackIndex] || submittedContacts[fallbackIndex];
  }

  private saveOperationContactNotificationRecipients(operationContactId: string, formContact: any): Observable<any> {
    const notificationTypeIds = (formContact?.operationContactNotifications || [])
      .filter((notificationType: any) => notificationType[Object.keys(notificationType)[0]] === true)
      .map((notificationType: any) => Object.keys(notificationType)[0]);

    if (!notificationTypeIds.length) {
      return of([]);
    }

    return forkJoin(
      notificationTypeIds.map((notificationTypeId: string) =>
        this.notificationRecipientService.addNotificationRecipientByOperationContactId({
          notificationOperationContactId: operationContactId,
          notificationOperationId: this.operation.operationId,
          notificationTypeId,
          notificationRecipientEmail: formContact.operationContactEmail
        })
      )
    );
  }

  removeOperationContact(idx: number) {
    if (this.operationContacts[idx]?.operationContactId) {
      this.operationContactsToRemove.push(this.operationContacts[idx].operationContactId);
    }
    this.operationContacts.splice(idx, 1);
    let formGroup = this.operationForm?.controls?.operationContacts as FormArray;
    if (formGroup?.at(idx)) {
      formGroup.at(idx).clearValidators();
      formGroup.at(idx).updateValueAndValidity();
      formGroup.removeAt(idx);
    }
    this.operationContacts.forEach((operationContact, idx) => {
      this.operationContacts[idx].operationContactOrder = idx + 1;
    });
  }
  /**
   * A function to validate controls,
   * and if there are any validation errors,
   * bounce the user to the top.
   */
  validateControls(): boolean {
    this.operationForm?.markAllAsTouched();
    this.operationForm?.updateValueAndValidity();

    if (!this.operationForm || this.operationForm.valid) {
      return true;
    }

    const firstError = this.getFirstInvalidControlElement(this.operationForm);

    if (firstError) {
      const scrollTarget = (firstError.closest('ion-item, .form-row, .operation-contact') as HTMLElement | null) || firstError;
      scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.focusValidationElement(firstError);
      alert('Unable to save. Please check ' + this.getValidationFieldLabel(firstError) + '.');
      return false;
    }

    alert('Unable to save. Please check the highlighted required fields. Your entries have been preserved.');
    return false;
  }

  private getFirstInvalidControlElement(control: FormGroup | FormArray, path: string[] = []): HTMLElement | null {
    for (const controlName of Object.keys(control.controls)) {
      const childControl = control.get(controlName);
      const childPath = [...path, controlName];

      if (childControl instanceof FormGroup || childControl instanceof FormArray) {
        const nestedElement = this.getFirstInvalidControlElement(childControl, childPath);
        if (nestedElement) {
          return nestedElement;
        }
      } else if (childControl?.invalid) {
        const selector = '[formControlName="' + controlName + '"]';
        const candidates = Array.from(document.querySelectorAll(selector)) as HTMLElement[];
        const matchingIndex = path.find(segment => /^\d+$/.test(segment));
        return candidates[matchingIndex ? Number(matchingIndex) : 0] || null;
      }
    }

    return null;
  }

  private getValidationFieldLabel(element: HTMLElement): string {
    const label = element.closest('ion-item, .form-row, .operation-contact')?.querySelector('ion-label, label');
    const text = (label?.textContent || '').replace(/\*/g, '').replace(/\s+/g, ' ').trim().replace(/:$/, '');
    return text || 'the first highlighted field';
  }

  private focusValidationElement(element: HTMLElement) {
    const focusTarget = (element.matches('input, textarea, select, button, ion-input, ion-select, ion-textarea, ion-radio, ion-checkbox')
      ? element
      : element.querySelector('input, textarea, select, button, ion-input, ion-select, ion-textarea, ion-radio, ion-checkbox')) as
      | (HTMLElement & { setFocus?: () => Promise<void> })
      | null;

    if (focusTarget?.setFocus) {
      void focusTarget.setFocus();
    } else {
      focusTarget?.focus();
    }
  }

  private formatPhoneInputValue(phoneValue: string): string {
    if (!phoneValue) {
      return '';
    }

    const digits = phoneValue.toString().replace(/[^0-9]/g, '');

    if (digits.length === 7) {
      return digits.substr(0, 3) + '-' + digits.substr(3);
    }

    if (digits.length === 10) {
      return digits.substr(0, 3) + '-' + digits.substr(3, 3) + '-' + digits.substr(6);
    }

    return phoneValue.toString();
  }

  addOperationGroupForm() {
    this.addOperationGroupModalOn = true;
    this.addOperationGroupFormControl = this.fb.group({
      operationGroupName: this.fb.control(''),
      operationGroupShortName: this.fb.control('')
    });
  }

  closeOperationGroupForm() {
    this.addOperationGroupModalOn = false;
  }

  addOperationGroup() {
    let formSubmission = this.addOperationGroupFormControl.getRawValue();
    var operationGroupName = formSubmission.operationGroupName;
    var operationGroupShortName = formSubmission.operationGroupShortName;
    this.operationService
      .addNewOperationGroup(operationGroupName, operationGroupShortName)
      .subscribe((operationGroup: any) => {
        if (operationGroup) {
          this.toastr.success('Successfully added operation group');
          this.operationGroups.push(operationGroup[0]);
          this.addOperationGroupModalOn = false;
          this.cdr.detectChanges();
        } else {
          alert('Oops! Something went wrong, please contact your tech support');
          this.addOperationGroupModalOn = false;
        }
      });
  }

  ngOnDestroy() {
    this.operationContacts = null;
    this.operationContactsOriginal = null;
  }
}
